// A video made from a finished cover: what /studio edits, what it saves on
// the Conversion (`studio`, see models/Conversion.ts) and what the export
// draws. Times are song seconds; positions are fractions of the frame.
import type { TemplateId } from "./templates";
import {
  styledAs,
  type CaptionStyle,
  type CaptionWord,
  type Dance,
  type Entrance,
} from "./layers";

export type Format = "9:16" | "1:1" | "16:9";

export const FORMATS: Record<
  Format,
  { label: string; note: string; w: number; h: number }
> = {
  "9:16": {
    label: "Vertical",
    note: "TikTok, Reels, Shorts",
    w: 1080,
    h: 1920,
  },
  "1:1": { label: "Square", note: "Instagram feed", w: 1080, h: 1080 },
  "16:9": { label: "Wide", note: "YouTube", w: 1920, h: 1080 },
};

export type BackgroundKind =
  | "stars"
  | "embers"
  | "fluid"
  | "voice"
  | "gradient"
  | "image";

export const GRADIENTS: [string, string][] = [
  ["#2E0F52", "#782DC8"],
  ["#0B0B1A", "#2E0F52"],
  ["#1A0B2E", "#C2185B"],
  ["#0F3460", "#16C79A"],
  ["#2D132C", "#EE4540"],
  ["#111111", "#3A3A3A"],
  ["#4A2380", "#D1FE17"],
  ["#FF7A59", "#FFD166"],
];

export type VideoProject = {
  v: 1;
  /** The look (lib/videostudio/templates.ts). */
  template: TemplateId;
  format: Format;
  /** The stretch of the song the video covers. */
  trim: { start: number; end: number };
  /** Gains, 0..1.5; 1 is the cover as it was mixed. */
  mix: { vocals: number; music: number };
  captions: CaptionStyle & { on: boolean };
  singers: {
    on: boolean;
    mode: "center" | "sides";
    size: number;
    y: number;
    entrance: Entrance;
    dance: Dance;
    names: boolean;
  };
  background: {
    kind: BackgroundKind;
    gradient: number;
    imageUrl?: string;
    blur: number;
    dim: number;
  };
};

/** The length a new project starts at: one TikTok-sized chorus. */
const FIRST_TRIM_SECS = 30;

export function newProject(duration: number): VideoProject {
  return {
    v: 1,
    template: "spotlight",
    format: "9:16",
    trim: {
      start: 0,
      end: Math.min(duration || FIRST_TRIM_SECS, FIRST_TRIM_SECS),
    },
    mix: { vocals: 1, music: 1 },
    captions: {
      on: true,
      ...styledAs("karaoke", { x: 0.5, y: 0.8, align: "center" }),
    },
    singers: {
      on: true,
      mode: "center",
      size: 0.24,
      y: 0.42,
      entrance: "pop",
      dance: "breathe",
      names: true,
    },
    background: { kind: "voice", gradient: 0, blur: 0.06, dim: 0.45 },
  };
}

/** A saved project, filled out with today's defaults for anything it predates. */
export function loadProject(saved: unknown, duration: number): VideoProject {
  const base = newProject(duration);
  if (!saved || typeof saved !== "object" || (saved as VideoProject).v !== 1)
    return base;
  const s = saved as Partial<VideoProject>;
  return {
    ...base,
    ...s,
    trim: { ...base.trim, ...s.trim },
    mix: { ...base.mix, ...s.mix },
    captions: { ...base.captions, ...s.captions },
    singers: { ...base.singers, ...s.singers },
    background: { ...base.background, ...s.background },
  };
}

export function frameSize(
  format: Format,
  height1080: boolean,
): { w: number; h: number } {
  const full = FORMATS[format];
  const k = height1080 ? 1 : 720 / 1080;
  // H.264 wants even dimensions.
  const even = (n: number) => Math.round((n * k) / 2) * 2;
  return { w: even(full.w), h: even(full.h) };
}

export type TranscriptionSegment = {
  start: number;
  end: number;
  text: string;
  words?: { word: string; start: number; end: number }[];
};

/**
 * The cover's transcription (outputUrls.transcription, Whisper segments from
 * the preprocessing API) as caption words. A segment without word timings is
 * spread evenly over its own time.
 */
export function wordsOf(
  segments: TranscriptionSegment[] | null | undefined,
): CaptionWord[] {
  if (!segments) return [];
  const words: CaptionWord[] = [];
  for (const seg of segments) {
    if (seg.words?.length) {
      for (const w of seg.words) {
        const text = String(w.word ?? "").trim();
        if (text) words.push({ text, start: w.start, end: w.end });
      }
      continue;
    }
    const texts = String(seg.text ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const step = (seg.end - seg.start) / Math.max(texts.length, 1);
    texts.forEach((text, i) =>
      words.push({
        text,
        start: seg.start + i * step,
        end: seg.start + (i + 1) * step,
      }),
    );
  }
  return words.sort((a, b) => a.start - b.start);
}

/** Who sings when, as the conversion records it (voice = the model's urlParam). */
export type VoiceTime = { voice: string; start: number; end: number };

/** Index into voiceTimes of the stretch sung at `t`, or -1 between them. */
export function stretchAt(voiceTimes: VoiceTime[], t: number): number {
  return voiceTimes.findIndex((s) => t >= s.start && t < s.end);
}
